import { TimelineEntry, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline';
import { buildTimelineEntryForTransferReviews, buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { TransferStatus } from '~/lib/transfers';
import { Counterparty, IntlWireTransfer } from '~/repositories';
import { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';

const internationalWireEvents = [
  {
    label: 'Created',
    field: 'createdAt',
  },
  {
    label: 'Pending Approval',
    field: 'preReviewAt',
  },
  {
    label: 'Initiated',
    field: 'initiatedAt',
  },
  {
    label: 'Pending Submission',
    field: 'pendingSubmissionAt',
    status: TimelineStatus.Pending,
  },
  {
    label: 'Submitted',
    field: 'submittedAt',
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Rejected',
    field: 'rejectedAt',
    status: TimelineStatus.Danger,
  },
];

export const buildInternationalWireEvents = ({
  transfer,
  account,
  counterparty,
}: {
  transfer: IntlWireTransfer;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  counterparty: Counterparty;
}): TimelineEntry[] => {
  const rejected = (transfer.status?.toLowerCase() as TransferStatus) === TransferStatus.REJECTED;
  const events = internationalWireEvents
    .filter(
      (event) =>
        event.field === 'createdAt' ||
        (!rejected && event.field === 'completedAt') ||
        !!transfer[event.field as keyof typeof transfer]
    )
    .map((event) => {
      if (event.field === 'preReviewAt') {
        return buildTimelineEntryForTransferReviews(
          transfer[event.field as keyof typeof transfer] as string | undefined,
          []
        );
      }

      let amount;
      if (transfer.amount) {
        amount = {
          value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
          isAdding: event.field === 'completedAt',
          currencyCode: transfer.currencyCode ?? 'USD',
          settledAmount: transfer.settledAmount ?? undefined,
          settledCurrencyCode: transfer.settledCurrencyCode,
          instructedCurrencyCode: transfer.instructedCurrencyCode,
          instructedAmount: transfer.instructedAmount,
          isIncoming: transfer.isIncoming,
        };
      }

      const date = (transfer[event.field as keyof typeof transfer] as string | undefined)
        ? new Date(transfer[event.field as keyof typeof transfer] as string)
        : undefined;

      const partyData =
        event.field === 'createdAt' || event.field === 'completedAt'
          ? buildTransferParty({
              account,
              counterparty,
              isIncoming: transfer.isIncoming ?? false,
              isOriginator: event.field === 'createdAt',
            })
          : undefined;

      const defaultStatus = rejected ? TimelineStatus.Danger : TimelineStatus.Pending;
      const status =
        event.field === 'pendingSubmissionAt' && transfer.pendingSubmissionAt && transfer.submittedAt
          ? TimelineStatus.Success
          : !!transfer[event.field as keyof typeof transfer]
            ? event.status
            : defaultStatus;

      return {
        label: event.label,
        amount,
        date: date,
        partyData,
        status,
      };
    });

  if (rejected) {
    events.push({
      label: 'Rejected',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: false,
            currencyCode: transfer.currencyCode ?? 'USD',
            settledAmount: transfer.settledAmount ?? undefined,
            settledCurrencyCode: transfer.settledCurrencyCode,
            instructedCurrencyCode: transfer.instructedCurrencyCode,
            instructedAmount: transfer.instructedAmount,
            isIncoming: transfer.isIncoming,
          }
        : undefined,
      status: TimelineStatus.Danger,
      partyData: buildTransferParty({
        account,
        counterparty,
        isIncoming: transfer.isIncoming ?? false,
        isOriginator: false,
      }),
      date: transfer.updatedAt ? new Date(transfer.updatedAt) : undefined,
    });
  }

  if (transfer.returnedAt) {
    events.push({
      label: 'Returned',
      amount: transfer.returnedAmount
        ? {
            value:
              typeof transfer.returnedAmount === 'string'
                ? parseFloat(transfer.returnedAmount)
                : transfer.returnedAmount,
            isAdding: false,
            currencyCode: transfer.returnedCurrencyCode ?? transfer.currencyCode ?? 'USD',
          }
        : undefined,
      status: TimelineStatus.Returned,
      date: transfer.returnedAt ? new Date(transfer.returnedAt) : undefined,
      internationalWireReturnData: transfer.returnReason
        ? {
            reason: transfer.returnReason,
            amount:
              typeof transfer.returnedAmount === 'string'
                ? parseFloat(transfer.returnedAmount)
                : (transfer.returnedAmount ?? 0),
            currencyCode: transfer.returnedCurrencyCode ?? transfer.currencyCode ?? 'USD',
          }
        : undefined,
      returnData: undefined,
    });
  }

  return events;
};
